import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FaFileAlt, FaDownload, FaFilter, FaSearch, FaChartLine } from 'react-icons/fa';
import api from '../../services/api';

// jQuery and DataTables are loaded via CDN in index.html
const $ = window.jQuery;

const UserReports = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState(null);
    const [filters, setFilters] = useState({
        start_date: '',
        end_date: '',
        type: 'all',
        status: 'all',
        search: ''
    });
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);

    useEffect(() => {
        loadTransactions();
    }, []);

    useEffect(() => {
        if (transactions.length > 0 && tableRef.current && !dataTableRef.current) {
            initializeDataTable();
        }
    }, [transactions]);

    const loadTransactions = async () => {
        try {
            setLoading(true);
            setError('');

            const params = new URLSearchParams();
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);
            if (filters.type && filters.type !== 'all') params.append('type', filters.type);
            if (filters.status && filters.status !== 'all') params.append('status', filters.status);
            if (filters.search) params.append('search', filters.search);

            const response = await api.get(`/api/reports/user/transactions?${params.toString()}`);
            
            if (response.data.success) {
                setTransactions(response.data.data || []);
                setSummary(response.data.summary || null);
                
                if (dataTableRef.current) {
                    dataTableRef.current.destroy();
                    dataTableRef.current = null;
                }
                
                setTimeout(() => {
                    initializeDataTable();
                }, 100);
            } else {
                setError(response.data.message || 'Failed to load transactions');
            }
        } catch (err) {
            console.error('Error loading transactions:', err);
            setError(err.response?.data?.message || 'Failed to load transactions');
        } finally {
            setLoading(false);
        }
    };

    const initializeDataTable = () => {
        if (tableRef.current && !dataTableRef.current && typeof $ !== 'undefined' && $.fn.DataTable) {
            dataTableRef.current = $(tableRef.current).DataTable({
                data: transactions,
                columns: [
                    {
                        data: 'created_at',
                        title: 'Date',
                        render: function(data) {
                            if (!data) return '-';
                            const date = new Date(data);
                            return date.toLocaleString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            });
                        }
                    },
                    {
                        data: 'transaction_id',
                        title: 'Transaction ID'
                    },
                    {
                        data: 'type',
                        title: 'Type',
                        render: function(data) {
                            const typeColors = {
                                'recharge': 'primary',
                                'dmt': 'success',
                                'bill_payment': 'info',
                                'wallet': 'warning'
                            };
                            const color = typeColors[data] || 'secondary';
                            return `<span class="badge bg-${color}">${data.toUpperCase()}</span>`;
                        }
                    },
                    {
                        data: 'service_type',
                        title: 'Service'
                    },
                    {
                        data: 'mobile',
                        title: 'Mobile/Account',
                        render: function(data) {
                            return data || '-';
                        }
                    },
                    {
                        data: 'operator',
                        title: 'Operator/Bank'
                    },
                    {
                        data: 'amount',
                        title: 'Amount',
                        render: function(data) {
                            return `₹${parseFloat(data || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        }
                    },
                    {
                        data: 'status',
                        title: 'Status',
                        render: function(data) {
                            const statusColors = {
                                'success': 'success',
                                'completed': 'success',
                                'approved': 'success',
                                'failed': 'danger',
                                'rejected': 'danger',
                                'pending': 'warning',
                                'processing': 'info'
                            };
                            const color = statusColors[data?.toLowerCase()] || 'secondary';
                            return `<span class="badge bg-${color}">${data || 'N/A'}</span>`;
                        }
                    },
                    {
                        data: 'failure_reason',
                        title: 'Remarks',
                        render: function(data) {
                            return data ? `<span title="${data}">${data.substring(0, 30)}${data.length > 30 ? '...' : ''}</span>` : '-';
                        }
                    }
                ],
                order: [[0, 'desc']],
                pageLength: 25,
                lengthMenu: [[10, 25, 50, 100, -1], [10, 25, 50, 100, 'All']],
                dom: 'Bfrtip',
                buttons: [
                    'copy', 'csv', 'excel', 'pdf', 'print'
                ],
                responsive: true,
                scrollX: true,
                language: {
                    search: 'Search:',
                    lengthMenu: 'Show _MENU_ entries',
                    info: 'Showing _START_ to _END_ of _TOTAL_ entries',
                    infoEmpty: 'No entries to show',
                    infoFiltered: '(filtered from _MAX_ total entries)'
                }
            });
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleApplyFilters = (e) => {
        e.preventDefault();
        loadTransactions();
    };

    const handleResetFilters = () => {
        setFilters({
            start_date: '',
            end_date: '',
            type: 'all',
            status: 'all',
            search: ''
        });
        setTimeout(() => {
            loadTransactions();
        }, 100);
    };

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0">
                    <FaFileAlt className="me-2" />
                    My Transaction Reports
                </h2>
            </div>

            {/* Summary Cards */}
            {summary && (
                <Row className="mb-4">
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <h5 className="text-muted mb-2">Total Transactions</h5>
                                <h3 className="mb-0">{summary.total_transactions || 0}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <h5 className="text-muted mb-2">Total Amount</h5>
                                <h3 className="mb-0 text-success">
                                    ₹{parseFloat(summary.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <h5 className="text-muted mb-2">Success</h5>
                                <h3 className="mb-0 text-success">{summary.success_count || 0}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <h5 className="text-muted mb-2">Failed</h5>
                                <h3 className="mb-0 text-danger">{summary.failed_count || 0}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Filters */}
            <Card className="mb-4">
                <Card.Header>
                    <FaFilter className="me-2" />
                    Filters
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleApplyFilters}>
                        <Row>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Start Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="start_date"
                                        value={filters.start_date}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="end_date"
                                        value={filters.end_date}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Transaction Type</Form.Label>
                                    <Form.Select
                                        name="type"
                                        value={filters.type}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="all">All Types</option>
                                        <option value="recharge">Recharge</option>
                                        <option value="dmt">DMT</option>
                                        <option value="bill_payment">Bill Payment</option>
                                        <option value="wallet">Wallet Transfer</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        name="status"
                                        value={filters.status}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="success">Success</option>
                                        <option value="failed">Failed</option>
                                        <option value="pending">Pending</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Search</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="search"
                                        placeholder="Txn ID, Mobile..."
                                        value={filters.search}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <Button type="submit" variant="primary" className="me-2">
                                    <FaSearch className="me-1" />
                                    Apply Filters
                                </Button>
                                <Button type="button" variant="secondary" onClick={handleResetFilters}>
                                    Reset
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Error Alert */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Transactions Table */}
            <Card>
                <Card.Header>
                    <FaChartLine className="me-2" />
                    My Transactions ({transactions.length})
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Loading transactions...</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table
                                ref={tableRef}
                                className="table table-striped table-hover"
                                style={{ width: '100%' }}
                            >
                            </table>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default UserReports;

